/**
 * Enemy Manager
 * Handles enemy spawning, AI, and combat
 */

import { THREE } from "../config/ThreeConfig.js";
import { getConfiguredGLTFLoader } from "../utils/gltfLoader.js";
import { publicUrl } from "../utils/publicUrl.js";
import { isTestEnvironment } from "../utils/runtimeEnv.js";
import { getArtModeOverride, getCharacterArt } from "../assets/CharacterArt.js";

function toVector3(position) {
  if (!position) return null;
  if (position instanceof THREE.Vector3) return position;
  if (typeof position.x === "number" && typeof position.y === "number" && typeof position.z === "number") {
    return new THREE.Vector3(position.x, position.y, position.z);
  }
  return null;
}

const ENEMY_SHIP_GLB_URLS = {
  complianceinterceptor: publicUrl("glb/enemies/compliance_interceptor.glb"),
  acquisition: publicUrl("glb/enemies/acquisition.glb"),
  compoundinterest: publicUrl("glb/enemies/compound_interest.glb"),
  creativedestruction: publicUrl("glb/enemies/creative_destruction.glb"),
  liquidationengine: publicUrl("glb/enemies/liquidation_engine.glb"),
};

const BOSS_GLB_URLS = {
  marketmakeromega: publicUrl("glb/bosses/market_maker_omega.glb"),
};

function normalizeId(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

let _enemyGlowTexture = null;
function getEnemyGlowTexture() {
  if (_enemyGlowTexture) return _enemyGlowTexture;
  if (isTestEnvironment()) return null;
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Simple radial glow (chunky but readable).
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0.0, "rgba(255, 80, 120, 0.75)");
  g.addColorStop(0.25, "rgba(255, 80, 120, 0.32)");
  g.addColorStop(0.65, "rgba(255, 80, 120, 0.12)");
  g.addColorStop(1.0, "rgba(255, 80, 120, 0.0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestMipMapNearestFilter;
  tex.needsUpdate = true;
  _enemyGlowTexture = tex;
  return tex;
}

export class EnemyManager {
  constructor(game) {
    this.game = game;
    this.enemies = [];
    this.projectiles = [];
    this.explosions = [];

    this._glbCache = new Map(); // url -> Promise<THREE.Object3D>
    // Heavy fleet GLBs are shown as "nearby LOD" so the game stays smooth by default.
    // `_enemyGlbActiveInstances` tracks *visible* GLB models (draw calls), not merely attached ones.
    this._enemyGlbMaxInstances = 12;
    this._enemyGlbMaxCeiling = 16;
    this._enemyGlbActiveInstances = 0;
    this._enemyGlbPendingInstances = 0;
    this._enemyGlbAttachDistance = 1400;
    this._enemyGlbShowDistance = 2400;

    // Lightweight auto-scaler: use more GLBs when FPS is high, back off when low.
    this._perfAcc = 0;
    this._perfFrames = 0;
    this._lastPerfAdjustAt = 0;
  }
  
  async init() {
    // Warm the GLB cache in the background so enemies don't "pop" through placeholder shapes.
    if (!isTestEnvironment()) {
      const urls = Object.values(ENEMY_SHIP_GLB_URLS).filter(Boolean);
      const warm = () => {
        for (const url of urls) {
          this.loadGLB(url).catch(() => {});
        }
      };
      try {
        if (typeof requestIdleCallback === "function") requestIdleCallback(warm, { timeout: 1200 });
        else setTimeout(warm, 50);
      } catch {
        // ignore
      }
    }

    return Promise.resolve();
  }

  loadGLB(url) {
    if (!url) return Promise.reject(new Error("Missing GLB url"));
    if (this._glbCache.has(url)) return this._glbCache.get(url);

    const p = (async () => {
      const loader = await getConfiguredGLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        loader.load(url, resolve, undefined, reject);
      });
      const root = gltf?.scene || gltf?.scenes?.[0];
      if (!root) throw new Error("GLB contained no scene");
      return root;
    })();

    this._glbCache.set(url, p);
    return p;
  }

  getEnemyArt(type, { bossType = null } = {}) {
    const t = normalizeId(type);
    const b = normalizeId(bossType);

    // Standard enemies (doc-aligned): Compliance Interceptor (fighter) + Acquisition (heavy).
    if (t === "fighter" || t === "interceptor" || t === "compliance" || t === "complianceinterceptor") {
      return { glbUrl: ENEMY_SHIP_GLB_URLS.complianceinterceptor, targetSize: 18 };
    }
    if (t === "bomber" || t === "gunship" || t === "acquisition") {
      return { glbUrl: ENEMY_SHIP_GLB_URLS.acquisition, targetSize: 28 };
    }

    if (t === "compoundinterest") return { glbUrl: ENEMY_SHIP_GLB_URLS.compoundinterest, targetSize: 42 };
    if (t === "creativedestruction") return { glbUrl: ENEMY_SHIP_GLB_URLS.creativedestruction, targetSize: 36 };
    if (t === "liquidationengine") return { glbUrl: ENEMY_SHIP_GLB_URLS.liquidationengine, targetSize: 48 };
    if (t === "auditornode") return { glbUrl: ENEMY_SHIP_GLB_URLS.liquidationengine, targetSize: 18 };
    if (t === "omeganode") return { glbUrl: ENEMY_SHIP_GLB_URLS.liquidationengine, targetSize: 22 };

    // Boss visuals: add real boss GLBs where available.
    if (t === "boss" && b) {
      if (b === "marketmakeromega") {
        return {
          glbUrl: BOSS_GLB_URLS.marketmakeromega,
          targetSize: 260,
          rotation: { y: Math.PI },
          isBoss: true,
        };
      }
      if (b === "liquidationauditor") {
        return {
          glbUrl: ENEMY_SHIP_GLB_URLS.liquidationengine,
          targetSize: 118,
          rotation: { y: Math.PI },
          isBoss: true,
        };
      }
      return null;
    }

    return null;
  }

  attachBossModel(entity, art) {
    if (!entity || !art?.glbUrl) return;
    if (isTestEnvironment()) return;
    if (getArtModeOverride() === 0) return;
    if (entity._bossGlbAttached) return;

    this.loadGLB(art.glbUrl)
      .then((root) => {
        if (entity.isDestroyed) return;

        const model = root.clone(true);
        model.name = "boss-glb";

        // Center + scale to an expected gameplay footprint.
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
        model.position.sub(center);

        const target = Math.max(40, Number(art.targetSize) || 240);
        const scale = target / maxDim;
        model.scale.setScalar(scale);

        // Optional rotation overrides (asset-facing).
        const rot = art.rotation;
        if (rot && typeof rot === "object") {
          if (Number.isFinite(rot.x)) model.rotation.x += rot.x;
          if (Number.isFinite(rot.y)) model.rotation.y += rot.y;
          if (Number.isFinite(rot.z)) model.rotation.z += rot.z;
        }

        model.traverse((obj) => {
          if (!obj?.isMesh) return;
          obj.castShadow = false;
          obj.receiveShadow = false;
          const material = obj.material;
          if (material && typeof material === "object") {
            // Preserve authored material sidedness (double-siding can wash lighting on some assets).
            // Preserve authored GLB look: do not replace textures or flatten materials.
            // Only small runtime tweaks for readability.
            if ("fog" in material) material.fog = false;
            if (material.map) {
              material.map = material.map; // ensure map stays enabled
              try {
                material.map.colorSpace = THREE.SRGBColorSpace;
                material.map.needsUpdate = true;
              } catch {
                // ignore
              }
            }
            // If the asset already uses emissive maps, let it read a bit more.
            if (material.emissiveMap && "emissiveIntensity" in material) {
              const cur = Number(material.emissiveIntensity) || 0;
              material.emissiveIntensity = Math.max(cur, 0.25);
            }
            material.needsUpdate = true;
          }
        });

        entity.object3D.add(model);
        entity.modelRoot = model;
        entity._bossGlbAttached = true;

        // Hide procedural placeholder visuals once art is attached.
        this.setEnemyPlaceholderVisible(entity, false);
      })
      .catch(() => {
        // Best effort; keep placeholder geometry if GLB fails.
      });
  }

  attachEnemyModel(entity, art) {
    if (!entity || !art?.glbUrl) return;
    if (isTestEnvironment()) return;
    // Explicit `?art=0` disables GLB enemies (perf/debug).
    if (getArtModeOverride() === 0) return;
    if (this._enemyGlbActiveInstances + this._enemyGlbPendingInstances >= this._enemyGlbMaxInstances) return;
    // Throttle: decoding/parsing GLBs mid-fight can hitch; keep it limited.
    if (this._enemyGlbPendingInstances >= 4) return;

    this._enemyGlbPendingInstances++;

    this.loadGLB(art.glbUrl)
      .then((root) => {
        // Enemy may have been destroyed while the model was loading.
        if (entity.isDestroyed) return;

        // Clone so each enemy has its own transform tree.
        const model = root.clone(true);
        model.name = "enemy-glb";

        // Center + scale to an expected gameplay footprint.
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
        model.position.sub(center);

        const target = Math.max(10, Number(art.targetSize) || 20);
        const scale = target / maxDim;
        model.scale.setScalar(scale);

        // Mild tilt so silhouettes read.
        model.rotation.x = 0.12;

        model.traverse((obj) => {
          if (!obj?.isMesh) return;
          // Shadows are expensive and hard to read in space; keep fleet GLBs fast.
          obj.castShadow = false;
          obj.receiveShadow = false;
          const material = obj.material;
          if (material && typeof material === "object") {
            material.side = THREE.DoubleSide;
            // Preserve authored textured materials; only runtime tweaks for readability.
            if ("fog" in material) material.fog = false;
            if (material.map) {
              material.map = material.map; // ensure map stays enabled
              material.map.needsUpdate = true;
            }
            if (material.emissiveMap && "emissiveIntensity" in material) {
              const cur = Number(material.emissiveIntensity) || 0;
              material.emissiveIntensity = Math.max(cur, 0.25);
            }
            material.needsUpdate = true;
          }
        });

        entity.object3D.add(model);
        entity.modelRoot = model;
        entity._enemyGlbAttached = true;
        entity._enemyGlbVisible = true;
        this.setEnemyPlaceholderVisible(entity, false);
        this._enemyGlbActiveInstances++;
      })
      .catch(() => {
        // Best effort; keep placeholder geometry if GLB fails.
      })
      .finally(() => {
        this._enemyGlbPendingInstances = Math.max(0, this._enemyGlbPendingInstances - 1);
      });
  }

  setEnemyPlaceholderVisible(entity, isVisible) {
    if (!entity?.object3D) return;
    if (entity.mesh) entity.mesh.visible = isVisible;
    const outline = entity.object3D.getObjectByName?.("enemy-outline");
    if (outline) outline.visible = isVisible;
    const bossPlaceholder = entity.object3D.getObjectByName?.("boss-placeholder");
    if (bossPlaceholder) bossPlaceholder.visible = isVisible;
  }

  updateEnemyModelVisibility(entity, player) {
    if (!entity?._enemyGlbAttached || !entity?.modelRoot || !player?.object3D) return;
    const d = entity.object3D.position.distanceTo(player.object3D.position);

    // Small hysteresis so it doesn't flicker when hovering around the boundary.
    const showAt = this._enemyGlbShowDistance * 0.9;
    const hideAt = this._enemyGlbShowDistance;

    if (entity._enemyGlbVisible && d > hideAt) {
      entity._enemyGlbVisible = false;
      entity.modelRoot.visible = false;
      this.setEnemyPlaceholderVisible(entity, true);
      this._enemyGlbActiveInstances = Math.max(0, this._enemyGlbActiveInstances - 1);
    } else if (!entity._enemyGlbVisible && d < showAt) {
      if (this._enemyGlbActiveInstances >= this._enemyGlbMaxInstances) return;
      entity._enemyGlbVisible = true;
      entity.modelRoot.visible = true;
      this.setEnemyPlaceholderVisible(entity, false);
      this._enemyGlbActiveInstances++;
    }
  }

  maybeAttachEnemyGlb(entity, player) {
    if (!entity || entity.isDestroyed || entity.isBoss) return;
    const art = entity._enemyArt;
    if (!art?.glbUrl) return;
    if (entity._enemyGlbAttached) return;
    if (!player?.object3D) return;

    if (this._enemyGlbActiveInstances + this._enemyGlbPendingInstances >= this._enemyGlbMaxInstances) return;
    if (this._enemyGlbPendingInstances >= 4) return;

    const d = entity.object3D.position.distanceTo(player.object3D.position);
    if (d > this._enemyGlbAttachDistance) return;

    this.attachEnemyModel(entity, art);
  }
  
  spawnEnemy(type, position) {
    const spawnPosition = toVector3(position) || new THREE.Vector3(0, 0, -100);
    const enemy = new Enemy(this.game, type, spawnPosition);
    this.enemies.push(enemy);
    this.game.scene.add(enemy.object3D);

    const art = this.getEnemyArt(type);
    if (art) enemy._enemyArt = art;
    if (art) this.attachEnemyModel(enemy, art);
    return enemy;
  }
  
  spawnBoss(type, position) {
    const spawnPosition = toVector3(position) || new THREE.Vector3(0, 0, -100);
    const boss = new Boss(this.game, type, spawnPosition);
    this.enemies.push(boss);
    this.game.scene.add(boss.object3D);

    const art = this.getEnemyArt("boss", { bossType: type });
    if (art) this.attachBossModel(boss, art);

    this.game.soundManager?.playMusic?.('boss');

    // Boss intro transmission (script-aligned where available).
    const dm = this.game?.dialogueManager;
    if (dm) {
      dm.trigger?.(type, "intro");
      if (normalizeId(type) === "marketmakeromega") {
        // Extra sting from Rageburrow per Stage One script.
        setTimeout(() => dm.speak?.("honeybadger", "Nerd."), 900);
      }
    }
    
    // Trigger Boss Briefing
    const ui = this.game?.uiManager;
    if (ui && typeof ui.showPersonalityMatrix === "function") {
      const portraitUrl = getCharacterArt(type)?.profileUrl || null;
      Promise.resolve(
        ui.showPersonalityMatrix({
          name: boss.bossName,
          personality: boss.personality,
          quote: boss.bossQuote,
          portraitUrl,
        })
      ).catch(() => {});
    }
    
    return boss;
  }
  
  createProjectile(position, direction, speed, damage, isPlayerProjectile) {
    const projectile = new Projectile(position, direction, speed, damage, isPlayerProjectile, { game: this.game });
    return projectile;
  }

  createProjectileWithOptions(position, direction, speed, damage, isPlayerProjectile, options = {}) {
    const projectile = new Projectile(position, direction, speed, damage, isPlayerProjectile, { ...(options || {}), game: this.game });
    return projectile;
  }
  
  createExplosion(position, radius, damage) {
    const explosion = this.createExplosionEffect(position);
    
    // Damage enemies in radius
    for (const enemy of this.enemies) {
      const distance = enemy.object3D.position.distanceTo(position);
      if (distance < radius) {
        enemy.takeDamage(damage);
      }
    }
    
    return explosion;
  }
  
  createExplosionEffect(position) {
    const particleCount = 50;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];
    
    for (let i = 0; i < particleCount * 3; i += 3) {
      positions[i] = position.x;
      positions[i + 1] = position.y;
      positions[i + 2] = position.z;
      
      velocities.push({
        x: (Math.random() - 0.5) * 100,
        y: (Math.random() - 0.5) * 100,
        z: (Math.random() - 0.5) * 100,
      });
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      color: 0xff6600,
      size: 2,
      transparent: true,
    });
    
    const particles = new THREE.Points(geometry, material);
    this.game.scene.add(particles);
    
    const explosion = {
      object3D: particles,
      velocities,
      age: 0,
      maxAge: 2,
      update: function(deltaTime) {
        this.age += deltaTime;
        const positions = this.object3D.geometry.attributes.position.array;
        
        for (let i = 0; i < positions.length; i += 3) {
          positions[i] += this.velocities[i / 3].x * deltaTime;
          positions[i + 1] += this.velocities[i / 3].y * deltaTime;
          positions[i + 2] += this.velocities[i / 3].z * deltaTime;
        }
        
        this.object3D.geometry.attributes.position.needsUpdate = true;
        this.object3D.material.opacity = 1 - this.age / this.maxAge;
      },
      isExpired: function() {
        return this.age >= this.maxAge;
      },
    };
    
    this.explosions.push(explosion);
    return explosion;
  }
  
  update(deltaTime, player) {
    // Auto-scale enemy GLB budget based on measured FPS (keeps “optimal from jump”).
    const dt = Number(deltaTime) || 0.016;
    this._perfAcc += dt;
    this._perfFrames += 1;

    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    if (!this._lastPerfAdjustAt) this._lastPerfAdjustAt = now;
    if (now - this._lastPerfAdjustAt > 1200 && this._perfAcc > 0.2) {
      const fps = this._perfFrames / Math.max(1e-6, this._perfAcc);
      // Prefer visual consistency: don't downscale into placeholder/invisible enemies.
      // Only increase the budget when FPS is high.
      if (fps > 58) {
        this._enemyGlbMaxInstances = Math.min(this._enemyGlbMaxCeiling, this._enemyGlbMaxInstances + 1);
      }
      this._perfAcc = 0;
      this._perfFrames = 0;
      this._lastPerfAdjustAt = now;
    }

    // If the budget shrank, hide the farthest visible GLBs first.
    if (player?.object3D && this._enemyGlbActiveInstances > this._enemyGlbMaxInstances) {
      const visibles = this.enemies.filter((e) => e && e._enemyGlbVisible && e.modelRoot && e.object3D);
      visibles.sort(
        (a, b) =>
          b.object3D.position.distanceTo(player.object3D.position) -
          a.object3D.position.distanceTo(player.object3D.position)
      );
      for (const e of visibles) {
        if (this._enemyGlbActiveInstances <= this._enemyGlbMaxInstances) break;
        e._enemyGlbVisible = false;
        e.modelRoot.visible = false;
        this.setEnemyPlaceholderVisible(e, true);
        this._enemyGlbActiveInstances = Math.max(0, this._enemyGlbActiveInstances - 1);
      }
    }

    // Update enemies
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];
      enemy.update(deltaTime, player);

      this.maybeAttachEnemyGlb(enemy, player);
      this.updateEnemyModelVisibility(enemy, player);
      
      if (enemy.isDestroyed) {
        if (enemy.isBoss && this.game?.dialogueManager) {
          const bt = normalizeId(enemy.bossType);
          if (bt === "marketmakeromega") {
            this.game.dialogueManager.speak?.("marketmakeromega", "Liquidity... reduced...");
          }
          this.game.soundManager?.playMusic?.('main');
        }

        this.game.scene.remove(enemy.object3D);
        this.enemies.splice(i, 1);

        if (enemy._enemyGlbVisible) {
          this._enemyGlbActiveInstances = Math.max(0, this._enemyGlbActiveInstances - 1);
        }

        this.game.recordEnemyDestroyed?.(enemy);
        
        // Add score
        this.game.addScore(enemy.scoreValue);
        
        // Chance to drop power-up
        if (Math.random() < 0.2) {
          this.game.powerUpManager.spawnPowerUp(enemy.object3D.position);
        }
        
      // Create explosion effect with visual feedback
      const enemyType = enemy.isBoss ? 'boss' : (enemy.type === 'fighter' ? 'fighter' : 'normal');
      if (this.game.feedback && this.game.feedback.playEnemyExplosion) {
        this.game.feedback.playEnemyExplosion(enemy.object3D.position.clone(), enemyType);
      } else {
        // Fallback to basic explosion
        this.createExplosionEffect(enemy.object3D.position);
      }
	      this.game.soundManager?.playSFX?.('explosion');
      }
    }
    
    // Update projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];
      projectile.update(deltaTime);
      
      if (projectile.isExpired()) {
        this.game.scene.remove(projectile.object3D);
        this.projectiles.splice(i, 1);
      }
    }
    
    // Update explosions
    for (let i = this.explosions.length - 1; i >= 0; i--) {
      const explosion = this.explosions[i];
      explosion.update(deltaTime);
      
      if (explosion.isExpired()) {
        this.game.scene.remove(explosion.object3D);
        this.explosions.splice(i, 1);
      }
    }
    
    // Check for level complete (guarded by LevelManager spawn tracking)
    this.game.levelManager?.maybeCompleteLevel?.(this.enemies.length);
  }
  
  clearAll() {
    for (const enemy of this.enemies) {
      this.game.scene.remove(enemy.object3D);
    }
    this.enemies.length = 0;
    
    for (const projectile of this.projectiles) {
      this.game.scene.remove(projectile.object3D);
    }
    this.projectiles.length = 0;
  }
  
  dispose() {
    this.clearAll();
  }
}

/**
 * Base Enemy Class
 */
class Enemy {
  constructor(game, type, position) {
    this.game = game;
    this.type = type;
    const t = normalizeId(type);
    const base = {
      complianceinterceptor: { health: 30, speed: 44, damage: 10, score: 100 },
      acquisition: { health: 70, speed: 24, damage: 16, score: 220 },
      compoundinterest: { health: 120, speed: 14, damage: 22, score: 420 },
      creativedestruction: { health: 90, speed: 55, damage: 18, score: 350 },
      liquidationengine: { health: 150, speed: 32, damage: 26, score: 650 },
      auditornode: { health: 42, speed: 0, damage: 0, score: 120 },
      omeganode: { health: 56, speed: 0, damage: 0, score: 180 },
      fighter: { health: 30, speed: 44, damage: 10, score: 100 }, // alias
      bomber: { health: 70, speed: 24, damage: 16, score: 220 }, // alias
    }[t] || { health: 50, speed: 20, damage: 10, score: 200 };

    this.health = base.health;
    this.maxHealth = this.health;
    this.damage = base.damage;
    this.speed = base.speed;
    this.scoreValue = base.score;
    this.isDestroyed = false;
    
    this.object3D = new THREE.Group();
    this.mesh = null;
    this.position = position ? position.clone() : new THREE.Vector3(0, 0, -100);
    this.velocity = new THREE.Vector3(0, 0, this.speed);

    // Simple behavior tuning
    this._t = t;
    this._age = 0;
    this._fireChance = {
      complianceinterceptor: 0.012,
      acquisition: 0.02,
      compoundinterest: 0.018,
      creativedestruction: 0.016,
      liquidationengine: 0.022,
      auditornode: 0,
      omeganode: 0,
      fighter: 0.012,
      bomber: 0.02,
    }[t] || 0.01;
    // Arcade combat cadence: telegraphed shots (charge -> fire) instead of random-per-frame fire.
    this._shotCooldownSec = {
      complianceinterceptor: 1.05,
      acquisition: 1.55,
      compoundinterest: 1.35,
      creativedestruction: 1.25,
      liquidationengine: 1.45,
      auditornode: 99,
      omeganode: 99,
      fighter: 1.05,
      bomber: 1.55,
    }[t] || 1.2;
    this._chargeDurationSec = {
      complianceinterceptor: 0.45,
      acquisition: 0.65,
      compoundinterest: 0.6,
      creativedestruction: 0.5,
      liquidationengine: 0.55,
      auditornode: 0.1,
      omeganode: 0.1,
      fighter: 0.45,
      bomber: 0.65,
    }[t] || 0.5;
    this._shotTimer = 0.6 + Math.random() * 0.7;
    this._chargeTimer = 0;
    this._chargeMesh = null;
    this._glowSprite = null;
    this._glowBaseScale = 0;
    this._strafeAmp = {
      complianceinterceptor: 0.9,
      acquisition: 0.35,
      compoundinterest: 0.15,
      creativedestruction: 1.1,
      liquidationengine: 0.5,
      auditornode: 0,
      omeganode: 0,
      fighter: 0.9,
      bomber: 0.35,
    }[t] || 0.5;
    this._weaveFreq = {
      complianceinterceptor: 1.4,
      acquisition: 0.8,
      compoundinterest: 0.45,
      creativedestruction: 2.1,
      liquidationengine: 1.0,
      auditornode: 0,
      omeganode: 0,
      fighter: 1.4,
      bomber: 0.8,
    }[t] || 1.0;
    this._zigY = (t === "creativedestruction" || t === "liquidationengine");

    // Optional entry choreography plan (set by LevelManager).
    this._flightPlan = null;
    
    this.createMesh();
  }
  
  createMesh() {
    const t = normalizeId(this.type);
    // No visible placeholders in gameplay: spawn an invisible collider and let the GLB handle visuals.
    const sizes = {
      complianceinterceptor: [16, 10, 18],
      fighter: [16, 10, 18],
      acquisition: [28, 16, 30],
      bomber: [28, 16, 30],
      compoundinterest: [42, 28, 46],
      creativedestruction: [30, 22, 30],
      liquidationengine: [48, 34, 52],
      auditornode: [18, 18, 18],
      omeganode: [22, 22, 22],
    };
    const [w, h, d] = sizes[t] || [20, 14, 20];

    const colliderGeom = new THREE.BoxGeometry(w, h, d);
    const colliderMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    const collider = new THREE.Mesh(colliderGeom, colliderMat);
    collider.name = "enemy-collider";
    collider.castShadow = false;
    collider.receiveShadow = false;
    this.mesh = collider;
    this.object3D.add(collider);

    // Readability: add a subtle additive glow so enemies don't disappear into vibrant nebula.
    this._ensureGlowSprite(Math.max(w, h, d));
    
    this.object3D.position.copy(this.position);
  }

  _ensureGlowSprite(maxDim = 24) {
    if (this._glowSprite) return this._glowSprite;
    const tex = getEnemyGlowTexture();
    if (!tex) return null;

    const mat = new THREE.SpriteMaterial({
      map: tex,
      color: 0xff3355,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    });
    const sprite = new THREE.Sprite(mat);
    sprite.name = "enemy-glow";
    sprite.renderOrder = 5;
    sprite.frustumCulled = false;

    const s = THREE.MathUtils.clamp(Number(maxDim) * 2.1, 26, 128);
    sprite.scale.set(s, s, 1);
    this._glowBaseScale = s;

    this.object3D.add(sprite);
    this._glowSprite = sprite;
    return sprite;
  }

  _updateGlow(player) {
    const sprite = this._glowSprite;
    const mat = sprite?.material;
    if (!sprite || !mat) return;

    const dist = player?.object3D?.position?.isVector3
      ? this.object3D.position.distanceTo(player.object3D.position)
      : Infinity;

    const telegraph = (this._chargeTimer > 0)
      ? Math.max(0, Math.min(1, 1 - Math.max(0, this._chargeTimer) / Math.max(1e-6, this._chargeDurationSec)))
      : 0;
    const near = Number.isFinite(dist) ? Math.max(0, Math.min(1, 1 - dist / 1100)) : 0;

    const base = 0.22;
    const opacity = Math.max(0.14, Math.min(0.65, base + telegraph * 0.28 + near * 0.18));
    mat.opacity = opacity;
    mat.needsUpdate = true;

    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    const pulse = 1 + 0.05 * Math.sin(Number(now) * 0.008 + this._age * 2.0);
    const scale = Math.max(10, (this._glowBaseScale || 24)) * (1 + telegraph * 0.22) * pulse;
    sprite.scale.set(scale, scale, 1);
  }
  
  update(deltaTime, player) {
    if (this.isDestroyed) return;
    this._age += deltaTime;

    if (this._anchorBoss?.isDestroyed) {
      this.isDestroyed = true;
      return;
    }

    // Shield nodes are kinematic weak points anchored to a boss. They should
    // never chase the player or drift away from the readable target silhouette.
    if (this._anchorBoss?.object3D && !this._anchorBoss.isDestroyed) {
      const off = this._anchorOffset?.isVector3 ? this._anchorOffset : new THREE.Vector3(42, 0, 0);
      const angle = (Number(this._anchorAngle) || 0) + this._age * (Number(this._anchorSpeed) || 0.75);
      const orbit = new THREE.Vector3(
        off.x * Math.cos(angle) - off.z * Math.sin(angle),
        off.y,
        off.x * Math.sin(angle) + off.z * Math.cos(angle)
      );
      this.position.copy(this._anchorBoss.object3D.position).add(orbit);
      this.velocity.set(0, 0, 0);
      this.object3D.position.copy(this.position);
      this.object3D.rotation.y += deltaTime * 1.6;
      this.object3D.rotation.z += deltaTime * 0.9;
      this._updateGlow(player);
      return;
    }

    // Choreographed entry: follow a scripted path for a moment, then transition to normal AI.
    if (this._flightPlan && !this._flightPlan.done) {
      this._updateFlightPlan(deltaTime);
      // Keep it readable: don't shoot during the entry path.
      return;
    }
    
    if (player && player.object3D) {
      // Move towards player
      const direction = new THREE.Vector3()
        .subVectors(player.object3D.position, this.position)
        .normalize();
      
      this.velocity.lerp(direction.multiplyScalar(this.speed), 0.05);
    }

    // Add simple strafe/weave so fleet classes feel distinct (visual + aim variety)
    const weave = Math.sin(this._age * this._weaveFreq + this.position.z * 0.02);
    const strafe = weave * this._strafeAmp * 12;
    this.velocity.x += strafe * deltaTime;
    if (this._zigY) {
      const yWeave = Math.cos(this._age * (this._weaveFreq * 0.9) + this.position.x * 0.03) * (this._strafeAmp * 8);
      this.velocity.y += yWeave * deltaTime;
    }
    
    this.position.add(this.velocity.clone().multiplyScalar(deltaTime));
    this.object3D.position.copy(this.position);
    
    // Face direction of movement
    if (this.velocity.length() > 0.1) {
      const targetRotation = Math.atan2(this.velocity.x, this.velocity.z);
      this.object3D.rotation.y = targetRotation;
    }
    
    // Shoot at player (telegraphed).
    this._updateShooting(deltaTime, player);

    // Keep readability updated with telegraph + proximity.
    this._updateGlow(player);
  }

  _ensureChargeMesh() {
    if (this._chargeMesh) return this._chargeMesh;
    const geom = new THREE.SphereGeometry(2.2, 10, 10);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff3355,
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      fog: false,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.name = "enemy-shot-telegraph";
    mesh.position.set(0, 0, 14);
    mesh.visible = false;
    this.object3D.add(mesh);
    this._chargeMesh = mesh;
    return mesh;
  }

  _setChargeVisual(progress) {
    const mesh = this._ensureChargeMesh();
    const p = Math.max(0, Math.min(1, Number(progress) || 0));
    mesh.visible = p > 0;
    mesh.scale.setScalar(0.7 + p * 1.9);
    if (mesh.material) {
      mesh.material.opacity = 0.15 + p * 0.75;
      mesh.material.needsUpdate = true;
    }
  }

  _updateShooting(deltaTime, player) {
    if (!player?.object3D) return;

    // Stage One bonus objective: enemies sometimes target the civilian freighter.
    let target = player;
    const st = this.game?.levelManager?.objectiveState;
    const friendlies = this.game?.friendlies;
    if (st?.id === "protect_freighter" && Array.isArray(friendlies) && friendlies.length > 0) {
      let nearest = null;
      let bestD = Infinity;
      for (const f of friendlies) {
        if (!f?.object3D || f.isDestroyed) continue;
        const d = this.object3D.position.distanceTo(f.object3D.position);
        if (d < bestD) {
          bestD = d;
          nearest = f;
        }
      }
      // Not always — keep pressure on the player too.
      if (nearest && Math.random() < 0.55) target = nearest;
    }
    if (!target?.object3D) return;

    // Avoid firing when very far away; keeps waves readable.
    const dist = this.object3D.position.distanceTo(target.object3D.position);
    if (!Number.isFinite(dist) || dist > 1100) return;

    if (this._chargeTimer > 0) {
      this._chargeTimer -= deltaTime;
      const p = 1 - Math.max(0, this._chargeTimer) / Math.max(1e-6, this._chargeDurationSec);
      this._setChargeVisual(p);
      if (this._chargeTimer <= 0) {
        this._setChargeVisual(0);
        this.shoot(target);
        this._shotTimer = this._shotCooldownSec * (0.85 + Math.random() * 0.35);
      }
      return;
    }

    this._shotTimer -= deltaTime;
    if (this._shotTimer <= 0) {
      this._chargeTimer = this._chargeDurationSec;
      this._setChargeVisual(0.05);
    }
  }

  _updateFlightPlan(deltaTime) {
    const fp = this._flightPlan;
    if (!fp) return;
    fp.t = (Number(fp.t) || 0) + deltaTime;
    const dur = Math.max(0.1, Number(fp.duration) || 1);
    const u = Math.max(0, Math.min(1, fp.t / dur));

    const bez3 = (a, b, c, d, t) => {
      const it = 1 - t;
      const it2 = it * it;
      const t2 = t * t;
      return (a * it2 * it) + (3 * b * it2 * t) + (3 * c * it * t2) + (d * t2 * t);
    };

    const p0 = fp.p0;
    const p1 = fp.p1;
    const p2 = fp.p2;
    const p3 = fp.p3;
    if (p0 && p1 && p2 && p3) {
      const x = bez3(p0.x, p1.x, p2.x, p3.x, u);
      const y = bez3(p0.y, p1.y, p2.y, p3.y, u);
      const z = bez3(p0.z, p1.z, p2.z, p3.z, u);
      this.position.set(x, y, z);
      this.object3D.position.copy(this.position);
    }

    // Face along the tangent (simple look-forward).
    if (u < 0.999 && p0 && p1 && p2 && p3) {
      const u2 = Math.min(1, u + 0.01);
      const x2 = bez3(p0.x, p1.x, p2.x, p3.x, u2);
      const z2 = bez3(p0.z, p1.z, p2.z, p3.z, u2);
      const dx = x2 - this.position.x;
      const dz = z2 - this.position.z;
      if (Math.hypot(dx, dz) > 1e-3) {
        this.object3D.rotation.y = Math.atan2(dx, dz);
      }
    }

    if (u >= 1) {
      fp.done = true;
      this._flightPlan = null;
    }
  }
  
  shoot(target) {
    const mgr = this.game?.enemyManager;
    if (!mgr || typeof mgr.createProjectile !== "function") return;
    if (!this.game?.scene || typeof this.game.scene.add !== "function") return;
    if (!Array.isArray(this.game.projectiles)) this.game.projectiles = [];

    if (!target?.object3D?.position?.isVector3) return;
    const direction = new THREE.Vector3()
      .subVectors(target.object3D.position, this.position)
      .normalize();

    const projectile = mgr.createProjectile(
      this.position.clone(),
      direction,
      100,
      10,
      false
    );

    this.game.scene.add(projectile.object3D);
    this.game.projectiles.push(projectile);
    this.game.soundManager?.playSFX?.('enemy_fire');
  }
  
  takeDamage(amount) {
    const wasAlive = !this.isDestroyed;
    this.health -= amount;
    
    if (this.health <= 0) {
      this.isDestroyed = true;
      if (wasAlive) {
        this._anchorBoss?._onShieldNodeDestroyed?.(this);
      }
    }
  }
  
  getBoundingBox() {
    return new THREE.Box3().setFromObject(this.object3D);
  }
}

/**
 * Boss Class
 */
class Boss extends Enemy {
  constructor(game, type, position) {
    super(game, 'boss', position);
    this.bossType = type;
    this.isBoss = true;
    
    // Different health for different bosses
    const bossHealth = {
      interceptorcaptain: 260,
      shippingboss: 520,
      volatilityboss: 640,
      hostileboss: 720,
      ledgerboss: 900,
      liquidationauditor: 460,
      liquidationboss: 1200,
      marketmakeromega: 820,
    };
    this.health = bossHealth[type] || 500;
    this.maxHealth = this.health;
    this.damage = 25;
    this.scoreValue = 5000;

    // Boss Personality Matrix
    const bossPersonalities = {
      interceptorcaptain: { aggression: 55, equity: 20, stability: 50, chaos: 40 },
      shippingboss: { aggression: 50, equity: 75, stability: 70, chaos: 30 },
      volatilityboss: { aggression: 45, equity: 55, stability: 35, chaos: 75 },
      hostileboss: { aggression: 70, equity: 60, stability: 35, chaos: 70 },
      ledgerboss: { aggression: 40, equity: 85, stability: 70, chaos: 40 },
      liquidationauditor: { aggression: 62, equity: 90, stability: 82, chaos: 25 },
      liquidationboss: { aggression: 80, equity: 100, stability: 25, chaos: 90 },
      marketmakeromega: { aggression: 65, equity: 95, stability: 55, chaos: 80 },
    };
    this.personality = bossPersonalities[type] || { aggression: 50, equity: 50, stability: 50, chaos: 50 };
    
    this.bossName = {
      interceptorcaptain: 'ELITE INTERCEPTOR CAPTAIN',
      shippingboss: 'LANE ENFORCEMENT NODE',
      volatilityboss: 'VOLATILITY ANOMALY',
      hostileboss: 'HOSTILE MARKET WARDEN',
      ledgerboss: 'LEDGER CORE ADMIN',
      liquidationauditor: 'LIQUIDATION AUDITOR',
      liquidationboss: 'LIQUIDATION EVENT',
      marketmakeromega: 'MARKET MAKER OMEGA',
    }[type] || 'UNKNOWN BOSS';

    this.bossQuote = {
      interceptorcaptain: '"Compliance check: fail. You will be reconciled."',
      shippingboss: '"All shipments are collateral. Including you."',
      volatilityboss: '"Markets do not stabilize. They break."',
      hostileboss: '"You entered a hostile market. Now exit liquidity."',
      ledgerboss: '"The ledger must balance. Your life is a rounding error."',
      liquidationauditor: '"Your protection objective has been reclassified as a liability."',
      liquidationboss: '"Everything gets liquidated. Starting with your hull."',
      marketmakeromega: '"Price discovery has been suspended. Your valuation approaches zero."',
    }[type] || '"Prepare for termination."';

    this.phases = [0.75, 0.5, 0.25];
    this.currentPhase = 0;
    this._scriptPhaseIndex = 0;

    // Market Maker Omega behavior timers (role/actions doc).
    this._omega = {
      cloneCooldown: 3.5 + Math.random() * 1.2,
      waveCooldown: 5.5 + Math.random() * 1.5,
      controlCooldown: 2.2 + Math.random() * 0.9,
      shieldCooldown: 7.5 + Math.random() * 1.8,
      shieldUntil: 0,
      clonesUntil: 0,
      clones: [],
      phase: 0,
      enrage: false,
      shieldNodes: [],
      shieldNodesSpawned: false,
      shieldNodesActive: false,
    };

    this._auditor = {
      shielded: normalizeId(type) === "liquidationauditor",
      nodes: [],
      nodesSpawned: false,
      nodeMessageShown: false,
      burstCooldown: 1.6,
    };
    
    this.createBossMesh();
  }
  
  createBossMesh() {
    const bossSizes = {
      interceptorcaptain: 18,
      shippingboss: 30,
      volatilityboss: 28,
      hostileboss: 34,
      ledgerboss: 40,
      liquidationauditor: 44,
      liquidationboss: 52,
      marketmakeromega: 62,
    };
    
    const size = bossSizes[this.bossType] || 25;
    
    // Group placeholder visuals so GLB attach can hide them all at once.
    const placeholder = new THREE.Group();
    placeholder.name = "boss-placeholder";

    const geometry = new THREE.DodecahedronGeometry(size, 1);
    const material = new THREE.MeshPhongMaterial({
      color: 0xff00ff,
      emissive: 0x660066,
      emissiveIntensity: 0.5,
      shininess: 100,
    });

    const core = new THREE.Mesh(geometry, material);
    core.name = "boss-placeholder-core";
    core.castShadow = false;
    placeholder.add(core);

    // Add rotating rings
    for (let i = 0; i < 3; i++) {
      const ringGeometry = new THREE.TorusGeometry(size * 1.5, 2, 8, 32);
      const ringMaterial = new THREE.MeshPhongMaterial({
        color: 0xff00ff,
        emissive: 0xff00ff,
        emissiveIntensity: 0.3,
      });
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.name = "boss-placeholder-ring";
      ring.rotation.x = (i / 3) * Math.PI;
      placeholder.add(ring);
    }

    this.object3D.add(placeholder);
  }
  
  update(deltaTime, player) {
    if (this.isDestroyed) return;
    this._age = (Number(this._age) || 0) + deltaTime;
    
    // Rotate boss
    this.object3D.rotation.y += deltaTime * 0.5;
    this.object3D.rotation.x += deltaTime * 0.3;

    const bossId = normalizeId(this.bossType);
    if (bossId === "liquidationauditor") {
      this._updateAuditor(deltaTime, player);
    } else if (bossId === "marketmakeromega") {
      // MARKET MAKER OMEGA: choreography-driven cadence (role/actions doc).
      this._updateOmega(deltaTime, player);
    } else {
      // Shoot pattern
      if (Math.random() < 0.03) {
        this.shootPattern(player);
      }
    }

    // Randomly trigger generic quotes
    if (Math.random() < 0.005 && this.game.dialogueManager) {
      this.game.dialogueManager.triggerRandom(this.bossType);
    }
  }

  _spawnAnchoredNode(type, offset, index = 0, count = 1, { speed = 0.75, label = "shield-node" } = {}) {
    const mgr = this.game?.enemyManager;
    if (!mgr || typeof mgr.spawnEnemy !== "function") return null;

    const start = this.object3D.position.clone().add(offset);
    const node = mgr.spawnEnemy(type, start);
    if (!node) return null;
    node._anchorBoss = this;
    node._anchorOffset = offset.clone();
    node._anchorAngle = (index / Math.max(1, count)) * Math.PI * 2;
    node._anchorSpeed = speed;
    node.object3D.name = `${label}-${index}`;
    return node;
  }

  _updateAuditor(deltaTime, player) {
    const a = this._auditor;
    if (!a) return;

    if (!a.nodesSpawned) this._spawnAuditorShieldNodes();
    a.nodes = (a.nodes || []).filter((node) => node && !node.isDestroyed);
    a.shielded = a.nodes.length > 0;

    if (!a.shielded && !a.nodeMessageShown) {
      a.nodeMessageShown = true;
      this.game?.uiManager?.showMessage?.("AUDITOR SHIELD DOWN", "success", 1800);
      this.game?.dialogueManager?.speak?.("honeybadger", "I audited the auditor.");
    }

    a.burstCooldown -= deltaTime;
    if (a.burstCooldown <= 0) {
      a.burstCooldown = a.shielded ? 1.9 : 1.35;
      this._auditorBurst(player);
    }

    if (Math.random() < (a.shielded ? 0.012 : 0.02)) this.shootPattern(player);
  }

  _spawnAuditorShieldNodes() {
    const a = this._auditor;
    if (!a || a.nodesSpawned) return;
    a.nodesSpawned = true;

    const offsets = [
      new THREE.Vector3(-62, 24, 18),
      new THREE.Vector3(62, 18, 18),
      new THREE.Vector3(0, -42, -8),
    ];
    a.nodes = offsets
      .map((off, i) => this._spawnAnchoredNode("auditornode", off, i, offsets.length, {
        speed: 0.85,
        label: "auditor-shield-node",
      }))
      .filter(Boolean);

    if (a.nodes.length) {
      this.game?.uiManager?.showMessage?.("TARGET: AUDITOR SHIELD NODES", "warning", 2200);
      this.game?.dialogueManager?.speak?.("elephant", "Those nodes are feeding the shield. Remove them first.");
    }
  }

  _auditorBurst(player) {
    const mgr = this.game?.enemyManager;
    if (!mgr || !this.game?.scene || !Array.isArray(this.game?.projectiles)) return;

    const base = this.object3D.position.clone();
    const targetPos = player?.object3D?.position?.isVector3
      ? player.object3D.position
      : base.clone().add(new THREE.Vector3(0, 0, 1));
    const centerDir = new THREE.Vector3().subVectors(targetPos, base).normalize();
    if (centerDir.lengthSq() < 1e-6) centerDir.set(0, 0, 1);

    const spread = [-0.18, 0, 0.18];
    for (const yaw of spread) {
      const dir = centerDir.clone().applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw).normalize();
      const projectile = mgr.createProjectileWithOptions
        ? mgr.createProjectileWithOptions(base.clone(), dir, 105, 12, false, { color: 0xffaa33 })
        : mgr.createProjectile(base.clone(), dir, 105, 12, false);
      this.game.scene.add(projectile.object3D);
      this.game.projectiles.push(projectile);
    }
  }

  _onShieldNodeDestroyed(node) {
    const id = normalizeId(node?.type);
    if (id === "auditornode") {
      const a = this._auditor;
      if (!a) return;
      a.nodes = (a.nodes || []).filter((n) => n && n !== node && !n.isDestroyed);
      if (a.nodes.length === 0) a.shielded = false;
      return;
    }

    if (id === "omeganode") {
      const o = this._omega;
      if (!o) return;
      o.shieldNodes = (o.shieldNodes || []).filter((n) => n && n !== node && !n.isDestroyed);
      if (o.shieldNodes.length === 0) {
        o.shieldNodesActive = false;
        this.game?.uiManager?.showMessage?.("OMEGA SHIELD GRID BROKEN", "success", 1800);
        this.game?.dialogueManager?.speak?.("mantisshrimp", "Finally. An opening with taste.");
      }
    }
  }

  _updateOmega(deltaTime, player) {
    const o = this._omega;
    if (!o) return;

    // Update clone visuals (if any).
    if (o.clones && o.clones.length) {
      for (const c of o.clones) {
        if (!c) continue;
        c.rotation.y += deltaTime * 0.9;
      }
    }

    o.shieldNodes = (o.shieldNodes || []).filter((node) => node && !node.isDestroyed);
    if (o.shieldNodesActive && o.shieldNodes.length === 0) o.shieldNodesActive = false;

    // Timers. Later phases compress Omega's cadence without making movement sim-like.
    const pressure = o.enrage ? 1.65 : (Number(o.phase) >= 1 ? 1.25 : 1);
    o.cloneCooldown -= deltaTime * pressure;
    o.waveCooldown -= deltaTime * pressure;
    o.controlCooldown -= deltaTime * pressure;
    o.shieldCooldown -= deltaTime * pressure;

    const now = (globalThis.performance && performance.now) ? performance.now() / 1000 : Date.now() / 1000;

    // Shield wall window: reduces damage and makes the boss feel like it’s “controlling scarcity”.
    if (o.shieldCooldown <= 0) {
      o.shieldCooldown = 8.5 + Math.random() * 2.5;
      o.shieldUntil = now + 2.25;
    }

    // Fake clones: visual decoys (no collisions) that orbit briefly.
    if (o.cloneCooldown <= 0) {
      o.cloneCooldown = 4.2 + Math.random() * 1.6;
      o.clonesUntil = now + 2.6;
      this._spawnOmegaClones();
    }

    // Interceptor waves.
    if (o.waveCooldown <= 0) {
      o.waveCooldown = 6.4 + Math.random() * 2.4;
      this._launchOmegaInterceptorWave();
    }

    // Arena control attacks: periodic “lanes” of bullets.
    if (o.controlCooldown <= 0) {
      o.controlCooldown = 2.4 + Math.random() * 1.0;
      this._omegaControlAttack(player);
    }

    // Base pattern: occasional radial burst for spectacle.
    if (Math.random() < 0.018) this.shootPattern(player);

    // Toggle visibility of clone visuals based on window.
    const clonesActive = o.clonesUntil > now;
    if (o.clones && o.clones.length) {
      for (const c of o.clones) {
        if (!c) continue;
        c.visible = clonesActive;
      }
    }
  }

  _enterOmegaPhase(phase) {
    const o = this._omega;
    if (!o || phase <= (Number(o.phase) || 0)) return;
    o.phase = phase;
    if (this.game?.missionStats) {
      this.game.missionStats.omegaPhases = Math.max(Number(this.game.missionStats.omegaPhases) || 0, phase);
    }

    if (phase === 1) {
      o.shieldNodesActive = true;
      this._spawnOmegaShieldNodes();
      this.game?.uiManager?.showMessage?.("OMEGA PHASE 2: SHIELD NODES", "warning", 2400);
      this.game?.dialogueManager?.speak?.("admiralbulldog", "Outer generators deployed. Destroy the nodes!");
      return;
    }

    if (phase === 2) {
      o.enrage = true;
      o.controlCooldown = Math.min(o.controlCooldown, 0.9);
      o.waveCooldown = Math.min(o.waveCooldown, 1.8);
      this.game?.uiManager?.showMessage?.("OMEGA PHASE 3: LIQUIDATION EVENT", "warning", 2600);
      this.game?.dialogueManager?.speak?.("marketmakeromega", "Liquidation event authorized.");
    }
  }

  _spawnOmegaShieldNodes() {
    const o = this._omega;
    if (!o || o.shieldNodesSpawned) return;
    o.shieldNodesSpawned = true;

    const offsets = [
      new THREE.Vector3(-92, 30, 22),
      new THREE.Vector3(92, 30, 22),
      new THREE.Vector3(-60, -42, 0),
      new THREE.Vector3(60, -42, 0),
    ];
    o.shieldNodes = offsets
      .map((off, i) => this._spawnAnchoredNode("omeganode", off, i, offsets.length, {
        speed: 1.05,
        label: "omega-shield-node",
      }))
      .filter(Boolean);
    o.shieldNodesActive = o.shieldNodes.length > 0;
  }

  _spawnOmegaClones() {
    const o = this._omega;
    if (!o) return;

    // Create clones once and reuse.
    if (!o.clones || !o.clones.length) {
      o.clones = [];
      for (let i = 0; i < 2; i++) {
        const g = new THREE.Group();
        g.name = `omega-clone-${i}`;
        g.visible = false;

        // Use the GLB model if available, otherwise fall back to a simple silhouette.
        const src = this.modelRoot || this.object3D.getObjectByName?.("boss-placeholder");
        if (src) {
          const clone = src.clone(true);
          clone.traverse((obj) => {
            if (!obj?.isMesh) return;
            // IMPORTANT: `Object3D.clone(true)` shares material references. If we mutate them,
            // we can accidentally wash out the *main* boss model too (white/additive).
            // Clone per-mesh materials before applying the ghost/additive look.
            const m = obj.material;
            if (Array.isArray(m)) {
              obj.material = m.map((entry) =>
                entry && typeof entry.clone === "function" ? entry.clone() : entry
              );
            } else if (m && typeof m === "object" && typeof m.clone === "function") {
              obj.material = m.clone();
            }

            const mat = obj.material;
            if (Array.isArray(mat)) {
              for (const mm of mat) {
                if (!mm || typeof mm !== "object") continue;
                if ("transparent" in mm) mm.transparent = true;
                if ("opacity" in mm) mm.opacity = Math.min(Number(mm.opacity) || 1, 0.28);
                if ("depthWrite" in mm) mm.depthWrite = false;
                if ("toneMapped" in mm) mm.toneMapped = false;
                if ("blending" in mm) mm.blending = THREE.AdditiveBlending;
                mm.needsUpdate = true;
              }
            } else if (mat && typeof mat === "object") {
              if ("transparent" in mat) mat.transparent = true;
              if ("opacity" in mat) mat.opacity = Math.min(Number(mat.opacity) || 1, 0.28);
              if ("depthWrite" in mat) mat.depthWrite = false;
              if ("toneMapped" in mat) mat.toneMapped = false;
              if ("blending" in mat) mat.blending = THREE.AdditiveBlending;
              mat.needsUpdate = true;
            }
          });
          g.add(clone);
        }

        g.scale.multiplyScalar(0.92);
        this.object3D.add(g);
        o.clones.push(g);
      }
    }

    // Place them around the boss.
    const offsets = [
      new THREE.Vector3(-85, 18, 35),
      new THREE.Vector3(85, -12, 35),
    ];
    for (let i = 0; i < o.clones.length; i++) {
      const c = o.clones[i];
      if (!c) continue;
      const off = offsets[i] || offsets[0];
      c.position.copy(off);
    }
  }

  _launchOmegaInterceptorWave() {
    const mgr = this.game?.enemyManager;
    if (!mgr) return;

    const base = this.object3D.position.clone();
    const z = base.z + 180;
    const spawns = [
      { x: -110, y: 35, z },
      { x: -55, y: -10, z: z - 30 },
      { x: 55, y: 15, z: z - 30 },
      { x: 110, y: -25, z },
    ];

    for (const p of spawns) {
      try {
        mgr.spawnEnemy("complianceinterceptor", new THREE.Vector3(p.x, p.y, p.z));
      } catch {
        // ignore
      }
    }
  }

  _omegaControlAttack(player) {
    const mgr = this.game?.enemyManager;
    if (!mgr) return;

    const base = this.object3D.position.clone();
    const dir =
      player?.object3D?.position?.isVector3
        ? new THREE.Vector3().subVectors(player.object3D.position, base).normalize()
        : new THREE.Vector3(0, 0, 1);
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1);

    // “Scarcity wall”: a thin row of bolts across X.
    const count = 9;
    const span = 260;
    for (let i = 0; i < count; i++) {
      const t = (i / Math.max(1, count - 1)) * 2 - 1;
      const x = base.x + t * (span * 0.5);
      const y = base.y + (Math.sin((i + this._age) * 1.3) * 14);
      const pos = new THREE.Vector3(x, y, base.z);
      const projectile = mgr.createProjectileWithOptions
        ? mgr.createProjectileWithOptions(pos, dir, 95, 14, false, { color: 0xff66aa })
        : mgr.createProjectile(pos, dir, 95, 14, false);
      this.game.scene.add(projectile.object3D);
      this.game.projectiles.push(projectile);
    }
  }
  
  fire() {
    const direction = new THREE.Vector3(0, 0, -1);
    const projectile = this.game.enemyManager.createProjectile(
      this.position.clone(),
      direction,
      80,
      15,
      false
    );
    this.game.scene.add(projectile.object3D);
    this.game.projectiles.push(projectile);
    return projectile;
  }
  
  shootPattern(_player) {
    const numProjectiles = 8;
    
    for (let i = 0; i < numProjectiles; i++) {
      const angle = (i / numProjectiles) * Math.PI * 2;
      const direction = new THREE.Vector3(
        Math.cos(angle),
        Math.sin(angle) * 0.5,
        Math.sin(angle)
      ).normalize();
      
      const projectile = this.game.enemyManager.createProjectile(
        this.position.clone(),
        direction,
        80,
        15,
        false
      );
      
      this.game.scene.add(projectile.object3D);
      this.game.projectiles.push(projectile);
    }
  }

  takeDamage(amount) {
    const bossId = normalizeId(this.bossType);
    if (bossId === "liquidationauditor") {
      const a = this._auditor;
      if (a?.shielded && (a.nodes || []).some((node) => node && !node.isDestroyed)) {
        amount = Math.max(0, amount) * 0.18;
      }
    }

    // MARKET MAKER OMEGA: rotating shield wall windows reduce incoming damage.
    if (bossId === "marketmakeromega") {
      const o = this._omega;
      const now = (globalThis.performance && performance.now) ? performance.now() / 1000 : Date.now() / 1000;
      if (o && o.shieldUntil && now < o.shieldUntil) {
        amount = Math.max(0, amount) * 0.22;
      }
      if (o?.shieldNodesActive && (o.shieldNodes || []).some((node) => node && !node.isDestroyed)) {
        amount = Math.max(0, amount) * 0.25;
      }
    }

    super.takeDamage(amount);
    if (this.isDestroyed) {
      const attachedNodes = [
        ...((this._auditor?.nodes || [])),
        ...((this._omega?.shieldNodes || [])),
      ];
      for (const node of attachedNodes) {
        if (node) node.isDestroyed = true;
      }
      return;
    }
    
    // Trigger "Peak" dialogue on boss damage
    if (this.game.dialogueManager && Math.random() < 0.3) {
      this.game.dialogueManager.trigger(this.bossType, 'take_damage');
    }

    // Stage One scripted callouts (Market Maker Omega).
    if (bossId === "marketmakeromega" && this.maxHealth > 0) {
      const ratio = Math.max(0, this.health) / this.maxHealth;
      if (ratio <= 0.66) this._enterOmegaPhase(1);
      if (ratio <= 0.33) this._enterOmegaPhase(2);

      const dm = this.game?.dialogueManager;
      if (!dm) return;

      if (this._scriptPhaseIndex === 0 && ratio <= 0.75) {
        this._scriptPhaseIndex = 1;
        dm.speak?.("elephant", "Target generators on the outer ring.");
      } else if (this._scriptPhaseIndex === 1 && ratio <= 0.5) {
        this._scriptPhaseIndex = 2;
        dm.speak?.("admiralbulldog", "More fighters launching! Keep pressure on it!");
      } else if (this._scriptPhaseIndex === 2 && ratio <= 0.25) {
        this._scriptPhaseIndex = 3;
        dm.speak?.("logi7", "Suggest evasive behavior.");
        setTimeout(() => dm.speak?.("honeybadger", "Counter suggestion: forward."), 650);
      }
    }
  }
}

/**
 * Projectile Class
 */
class Projectile {
  static _sharedGeo = null;
  static _sharedTipGeo = null;
  static _sharedMats = null;

  static _ensureShared() {
    if (Projectile._sharedGeo) return;

    const beamLength = 14;
    const beamRadius = 0.28;
    const g = new THREE.PlaneGeometry(Math.max(0.01, beamRadius * 2.0), beamLength, 1, 1);
    g.rotateX(-Math.PI / 2);
    Projectile._sharedGeo = g;

    Projectile._sharedTipGeo = new THREE.SphereGeometry(beamRadius * 1.1, 8, 8);

    const mkMat = (color, opacity) => new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      fog: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });

    Projectile._sharedMats = {
      playerOuter: mkMat(0x00ffff, 0.18),
      playerInner: mkMat(0x00ffff, 0.82),
      playerTip: mkMat(0x00ffff, 0.85),
      enemyOuter: mkMat(0xff3355, 0.18),
      enemyInner: mkMat(0xff3355, 0.82),
      enemyTip: mkMat(0xff3355, 0.85),
      chargedOuter: mkMat(0x00ffff, 0.22),
      chargedInner: mkMat(0x00ffff, 0.9),
      chargedTip: mkMat(0x00ffff, 0.85),
    };
  }

  constructor(position, direction, speed, damage, isPlayerProjectile, options = {}) {
    this.position = position.clone();
    this.prevPosition = position.clone();
    const dir = (direction && direction.isVector3) ? direction.clone() : new THREE.Vector3(0, 0, -1);
    if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1);
    this.direction = dir.normalize();
    this.speed = speed;
    this.damage = damage;
    this.isPlayerProjectile = isPlayerProjectile;
    this.game = options?.game || null;
    this.isCharged = !!options?.isCharged;
    this.targeted = !!options?.targeted;
    this.homingTarget = options?.homingTarget || null;
    this.homingTurnRate = Math.max(0, Number(options?.homingTurnRate) || (this.isCharged ? 6.0 : 0));
    this.explosionRadius = Math.max(0, Number(options?.explosionRadius) || (this.isCharged ? 70 : 0));
    this.splashDamageFactor = Math.max(0, Math.min(1, Number(options?.splashDamageFactor) || (this.isCharged ? 0.65 : 0)));
    this.color = (typeof options?.color === "number") ? options.color : null;
    this.age = 0;
    this.maxAge = 5;
    this._trailAcc = 0;
    this._trailDistAcc = 0;

    this.object3D = new THREE.Group();

    // Orient the projectile so its local -Z axis matches its flight direction.
    this.object3D.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 0, -1),
      this.direction
    );
    
    this.createMesh();
  }
  
  createMesh() {
    Projectile._ensureShared();
    const isCharged = !!this.isCharged;
    const beamLength = isCharged ? 20 : (this.isPlayerProjectile ? 14 : 12);
    const beamRadius = isCharged ? 0.55 : (this.isPlayerProjectile ? 0.28 : 0.34);

    let outerMat, innerMat, tipMat;
    if (typeof this.color === "number" && this.color !== 0x00ffff && this.color !== 0xff3355) {
      const mkMat = (opacity) => new THREE.MeshBasicMaterial({
        color: this.color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        fog: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      });
      outerMat = mkMat(isCharged ? 0.22 : 0.18);
      innerMat = mkMat(isCharged ? 0.9 : 0.82);
      tipMat = mkMat(0.85);
    } else {
      const m = Projectile._sharedMats;
      if (isCharged) {
        outerMat = m.chargedOuter;
        innerMat = m.chargedInner;
        tipMat = m.chargedTip;
      } else if (this.isPlayerProjectile) {
        outerMat = m.playerOuter;
        innerMat = m.playerInner;
        tipMat = m.playerTip;
      } else {
        outerMat = m.enemyOuter;
        innerMat = m.enemyInner;
        tipMat = m.enemyTip;
      }
    }

    const geo = Projectile._sharedGeo;
    const tipGeo = Projectile._sharedTipGeo;

    const outerA = new THREE.Mesh(geo, outerMat);
    outerA.position.z = beamLength * 0.5;
    outerA.renderOrder = 20;
    outerA.frustumCulled = false;

    const outerB = new THREE.Mesh(geo, outerMat);
    outerB.position.z = beamLength * 0.5;
    outerB.rotation.z = Math.PI / 2;
    outerB.renderOrder = 20;
    outerB.frustumCulled = false;

    const innerA = new THREE.Mesh(geo, innerMat);
    innerA.position.z = beamLength * 0.5;
    innerA.renderOrder = 20;
    innerA.frustumCulled = false;

    const innerB = new THREE.Mesh(geo, innerMat);
    innerB.position.z = beamLength * 0.5;
    innerB.rotation.z = Math.PI / 2;
    innerB.renderOrder = 20;
    innerB.frustumCulled = false;

    const tipMesh = new THREE.Mesh(tipGeo, tipMat);
    tipMesh.position.z = 0;
    tipMesh.renderOrder = 21;
    tipMesh.frustumCulled = false;

    this.object3D.add(outerA, outerB, innerA, innerB, tipMesh);
    this.object3D.position.copy(this.position);
  }
  
  update(deltaTime) {
    this.age += deltaTime;
    this.prevPosition.copy(this.position);

    // Homing (used for charged lock-on shots).
    const target = this.homingTarget;
    if (this.homingTurnRate > 0 && target && !target.isDestroyed && target.object3D?.position?.isVector3) {
      const desired = new THREE.Vector3()
        .subVectors(target.object3D.position, this.position)
        .normalize();
      if (desired.lengthSq() > 1e-6) {
        // Frame-rate friendly turn: blend toward desired direction.
        const t = 1 - Math.exp(-this.homingTurnRate * Math.max(0, deltaTime));
        this.direction.lerp(desired, t).normalize();
        this.object3D.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, -1), this.direction);
      }
    }

    const step = this.direction.clone().multiplyScalar(this.speed * deltaTime);
    this.position.add(step);
    this.object3D.position.copy(this.position);

    // Chunky streak/trail (throttled for perf).
    const gm = this.game;
    const fb = gm?.feedback;
    if (this.isPlayerProjectile && fb && typeof fb.createWeaponTrail === "function") {
      const dist = step.length();
      this._trailAcc += deltaTime;
      this._trailDistAcc += dist;
      if (this._trailAcc >= 0.075 && this._trailDistAcc >= 18) {
        this._trailAcc = 0;
        this._trailDistAcc = 0;
        const c = this.isPlayerProjectile ? 0x00ffff : 0xff3355;
        try {
          fb.createWeaponTrail(this.prevPosition.clone(), this.position.clone(), c);
        } catch {
          // ignore
        }
      }
    }
  }
  
  isExpired() {
    return this.age >= this.maxAge;
  }
  
  getBoundingBox() {
    return new THREE.Box3().setFromObject(this.object3D);
  }

  getSweptBoundingBox(padding = 1.2) {
    const r = Math.max(0.5, Number(padding) || 0);
    const min = new THREE.Vector3(
      Math.min(this.prevPosition.x, this.position.x) - r,
      Math.min(this.prevPosition.y, this.position.y) - r,
      Math.min(this.prevPosition.z, this.position.z) - r
    );
    const max = new THREE.Vector3(
      Math.max(this.prevPosition.x, this.position.x) + r,
      Math.max(this.prevPosition.y, this.position.y) + r,
      Math.max(this.prevPosition.z, this.position.z) + r
    );
    return new THREE.Box3(min, max);
  }
}
